<template>
  <div :class="['resolver', { 'resolver--failed': isFailed }]">
    <slot
      v-if="isFailed"
      name="failed"
      v-bind="{ onRetry }"
    />

    <template v-else-if="isResolved">
      <component
        :is="component"
        v-bind="componentAwaiterProps ?? {}"
      />
    </template>

    <slot
      v-else
      name="preloader"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, computed, type PropType } from '@/composition/vue/compositionApi'
import { useResolvers } from '@/composition/resolvers'
import type { AnyComponent } from '@/components/types'
import { getRouterService } from '@/core/container/ioc'
import type { Resolvers } from '@/services/types'

const props = defineProps({
  componentAwaiter: {
    type: Function as PropType<() => Promise<AnyComponent>>,
    required: true,
  },
  componentAwaiterProps: {
    type: Object,
  },
})
const emit = defineEmits<{
  'start-resolve': []
  'finish-resolve': []
}>()

const currentRoute = getRouterService().getCurrentRoute()
const routeName = currentRoute.name?.toString() || ''
const resolvers: Resolvers = (currentRoute.meta?.resolvers as Resolvers) ?? []
const nonBlockingResolvers: Resolvers = (currentRoute.meta?.nonBlockingResolvers as Resolvers) ?? []

const {
  resolveWithNonBlockingResolvers,
  unresolve,
  retry: onRetry,
  resolvingState,
  component,
} = useResolvers(routeName, resolvers, nonBlockingResolvers, props.componentAwaiter)

const resolve = async () => {
  emit('start-resolve')
  await resolveWithNonBlockingResolvers()
  emit('finish-resolve')
}

const isFailed = computed<boolean>(() => resolvingState.value === 'FAILED')
const isResolved = computed<boolean>(() => resolvingState.value === 'RESOLVED')

onMounted(() => {
  resolve()
})

onUnmounted(() => {
  unresolve()
})
</script>

<style lang="scss" scoped>
.resolver {
  height: 100%;

  &--failed {
    background-color: var(--tm-resolver-failed-bg-color, $white);
  }
}
</style>
